// ── THE WHEAT COVERAGE DECISION ──────────────────────────────────────────────
//
// 80% individual RP is the BENCHMARK, not a tie-break. Both alternatives must
// prove themselves against it, and every recommendation answers two questions
// explicitly: **why not 75%** and **why not 85%**.
//
// ── WHY THIS WAS REARCHITECTED (2026-09-01, second pass) ─────────────────────
//
// The first pass corrected the area index — the engine had been correlating the
// farm against NASS STATE wheat yield when SCO and ECO settle on the RMA COUNTY
// index. That correction stands. What it did NOT fix is that a single area
// measure was still deciding the underlying INDIVIDUAL coverage level, and the
// result showed it: the 75% bucket collapsed from 52 units to 3, and 40 units
// stepped to 85% on evidence that amounted to "volatile and poorly correlated".
//
// Neither of those is an economic argument. Raising the MPCI level does not buy
// more total protection — it moves a 5-point band off the county trigger and
// onto the farm's own. So the only question that decides it is whether the
// **incremental individual protection is worth its incremental cost**, and that
// is a calculation, not a grade.
//
// So the analysis is split, and the split is structural:
//
//   A  individualNeed.js  — how much downside exists on this farm's own record.
//                           Takes NO area argument.
//   B  areaFit.js         — how well the county trigger substitutes for it.
//   C  coverageStack.js   — what each band costs to move, NET of the SCO premium
//                           the grower stops paying.
//
// This module combines them. It is the only place the three meet.
//
// ── THE ECONOMIC TEST ────────────────────────────────────────────────────────
//
// For a band (lo, hi]:
//
//     E  = expected annual indemnity from that band, on the farm's own yield
//          record, priced at the projected price          (A)
//     m  = P(county trigger does NOT pay | farm had a loss)  = 1 − hitRate   (B)
//     C  = net marginal cost of moving the band to the individual trigger (C)
//
//     incremental value = E × m        ← the payout the county would NOT make
//     benefit/cost      = E × m ÷ C
//
// A ratio above 1 means the band pays for itself on this farm's record even
// after the county's substitution. That is the whole argument, and every term
// is printed.
//
// ⚠ m MUST BE MEASURED, NOT ASSUMED. Without it the value is bounded [0, E] and
// nothing can be affirmed. So an unmeasurable area fit does not "fail safe to
// 85%" — it means the case for 85% cannot be made and the anchor holds. The one
// exception is a band that costs nothing (`freeOrBetter`), where the grower is
// being handed individual coverage for no net premium.

import { areaFit, FIT, FIT_LABEL, SCO_TRIGGER_PCT, areaFitSentence } from "./areaFit.js";
import { individualNeed, bandExpectedIndemnity, NEED, NEED_LABEL, individualNeedSentence } from "./individualNeed.js";
import { buildLevels, marginalMove, ecoIsLevelInvariant, ANCHOR_LEVEL, CANDIDATE_LEVELS } from "./coverageStack.js";
import { candidateLevels, WHEAT_LEVELS, allLevels } from "./levelLadder.js";
import { resolveAcreageBasis, UNLOCKED_BY_ACREAGE, NOMINAL_QUOTE_ACRES, decidesCoverage } from "./acreageBasis.js";
import { revenuePlanOf } from "./revenueBacktest.js";
import { yieldProfile, chooseBandLevel, priceRatiosFrom, bandConfidence, EVID,
         BAND_MARGIN_TOP } from "./bandRules.js";

export { ANCHOR_LEVEL, CANDIDATE_LEVELS };

export const REC = { DOWN: "down", ANCHOR: "anchor", UP: "up" };

// ⚠ MARGINS, NOT THRESHOLDS ON A GRADE — and both are stated rather than tuned.
//
// The expected-indemnity term is measured over a median of 7 yield years, so it
// carries real estimation error. Requiring the benefit merely to EXCEED the cost
// would recommend a move on a coin-flip. These are the margins by which the
// evidence must beat break-even before a move off the anchor is recommended, and
// the RAW ratio is always reported so a human can see a 1.1 and decide for
// themselves.
export const MARGIN_UP = 1.25;      // 85% is the exception: 25% clear of break-even
export const MARGIN_DOWN = 1.25;    // dropping protection needs the same clear margin
export const MARGIN_DOWN_UNPROVEN = 2.0;  // ...and twice that when area fit is unmeasurable

// A prevent-plant share above this makes lowering the base level a decision with
// a named consequence rather than a premium saving: the PP guarantee is a
// fraction OF the base guarantee, so 80→75 lowers it too.
export const PP_MATERIAL_FRAC = 0.05;

// SCO/ECO economic attractiveness, graded on coverage bought per premium dollar.
// This is the SUBSIDY question and it is deliberately separate from area fit —
// an endorsement can be excellent value and a poor fit for one farm at once.
export const ECON = {
  HIGH: "HIGHLY_ATTRACTIVE", GOOD: "ATTRACTIVE",
  FAIR: "MODERATE", POOR: "POOR", NA: "NOT_OFFERED",
};
export const ECON_LABEL = {
  [ECON.HIGH]: "Highly attractive", [ECON.GOOD]: "Attractive",
  [ECON.FAIR]: "Moderate", [ECON.POOR]: "Poor", [ECON.NA]: "Not offered",
};
export function gradeEconomics(covPerAc, premPerAc) {
  if (covPerAc == null || premPerAc == null || premPerAc <= 0) return { grade: ECON.NA, ratio: null };
  const ratio = covPerAc / premPerAc;
  const grade = ratio >= 12 ? ECON.HIGH : ratio >= 7 ? ECON.GOOD : ratio >= 4 ? ECON.FAIR : ECON.POOR;
  return { grade, ratio: Math.round(ratio * 10) / 10 };
}

// ⚠ THE TWO SIDES SPELL A STATE DIFFERENTLY AND THE MISMATCH IS SILENT. Rate
// tables key on FIPS ("36|GENESEE|003"); sco_eco_county stores postal ("NY").
// Passing one for the other matches nothing, the fit reads INSUFFICIENT_HISTORY,
// and every farm holds the anchor "for want of history" while nine years sit
// unread. That is what the first cut did; check-wheat-recommend caught it.
export const FIPS_TO_POSTAL = { "18": "IN", "24": "MD", "36": "NY", "39": "OH", "42": "PA" };
export function postalStateOf(state) {
  const s = String(state == null ? "" : state).trim().toUpperCase();
  if (/^[A-Z]{2}$/.test(s)) return s;
  return FIPS_TO_POSTAL[s] || FIPS_TO_POSTAL[s.padStart(2, "0")] || null;
}

const r2 = (n) => (n == null || !isFinite(n) ? null : Math.round(n * 100) / 100);
const pct = (f) => (f == null ? "—" : Math.round(f * 100) + "%");

// ── the band case ────────────────────────────────────────────────────────────
//
// One band, fully worked: what it is worth, what it costs, and whether the
// evidence supports the move. Returns `verdict` plus every term.
export function bandCase({ band, expected, fit, move, direction }) {
  const E = expected && expected.perAcre != null ? expected.perAcre : null;
  const C = move ? move.netCostPerAc : null;
  const hit = fit && fit.hitRate != null ? fit.hitRate : null;
  const miss = hit != null ? 1 - hit : null;
  const value = (E != null && miss != null) ? E * miss : null;
  const ratio = (value != null && C != null && C > 0) ? value / C : null;

  const blockers = [];
  if (E == null) blockers.push("the band's expected indemnity is not priceable on this farm's history");
  if (miss == null) blockers.push("the county miss rate is not measured, so the value of moving this band cannot be bounded above zero");
  if (C == null) blockers.push("the marginal cost could not be priced");

  return {
    band, direction,
    expectedIndemnityPerAc: E,
    yearsPaying: expected ? expected.yearsPaying : null,
    yearsObserved: expected ? expected.years : null,
    countyHitRate: hit, countyMissRate: miss != null ? r2(miss) : null,
    incrementalValuePerAc: value != null ? r2(value) : null,
    netCostPerAc: C,
    naiveMpciOnlyPerAc: move ? move.naiveMpciOnlyPerAc : null,
    scoDisplacedPerAc: move ? move.scoDisplaced : null,
    benefitCostRatio: ratio != null ? Math.round(ratio * 100) / 100 : null,
    freeOrBetter: move ? move.freeOrBetter : null,
    blockers,
  };
}

// ── THE RENEWAL BASELINE IS THE INCUMBENT ELECTION ───────────────────────────
//
// Kevin, 2026-09-01, settling the architecture: *"For existing policies,
// incumbent election is the renewal decision baseline. 80% remains the model's
// analytical benchmark, not an automatic recommendation. … Require affirmative
// measurable evidence for any recommended change from the incumbent. A lack of
// evidence is not evidence to buy more coverage."*
//
// The previous pass answered *what coverage would Silas choose from scratch*.
// Over the live book that recommended **+$140,583 of premium, +36.4%, on 99
// units, with nothing moving down** — because 75% had to prove itself, half the
// book cannot measure the area evidence 75% needs, and the default therefore
// became "buy more". An engine that fails toward more premium whenever the
// evidence is thin is not neutral; it has a direction.
//
// So the question is now **should this farm CHANGE from its current election**,
// and the burden of proof sits on the change. 80% is still computed and shown
// as the analytical benchmark on every unit — it is what the alternatives are
// measured against — it simply is not a destination the engine walks to for
// want of a reason to stay.
//
// ⚠ KEEP CURRENT IS NOT INERTIA. It is only reachable when the comparison
// actually RAN: the band value was priceable and the county miss rate was
// measured. Where it was not, the answer is INSUFFICIENT_EVIDENCE_TO_CHANGE,
// which is a different fact and reads like one.
// The unit structures RMA writes, and the only strings this engine will rate at. An
// unrecognised one is refused rather than passed through — `quoteRP` treats anything it
// does not know as no discount band at all, which is a premium nobody can trace.
export const UNIT_CODES = ["EU", "BU", "OU", "UA"];

export const CLASSIFICATION = {
  // ⚠ NOT a kind of recommendation. The agent picked this level; the engine priced it.
  // Distinguishing the two is what stops a sheet presenting a human's judgement as an
  // evidenced result — and what lets a reader tell them apart later.
  AGENT: "AGENT_SELECTED",
  KEEP: "KEEP_CURRENT",
  C75: "CONSIDER_75",
  C80: "CONSIDER_80",
  C85: "CONSIDER_85",
  INSUFFICIENT: "INSUFFICIENT_EVIDENCE_TO_CHANGE",
  // ⚠ NOT a kind of "insufficient evidence". The evidence about this FARM may be
  // excellent; what is missing is the denominator every comparison divides by.
  // A grower clears it by typing one number, which is a different instruction
  // from "your history cannot support a change".
  ACREAGE: "ACREAGE_REQUIRED",
};
export const CLASSIFICATION_LABEL = {
  [CLASSIFICATION.AGENT]: "Agent selected",
  [CLASSIFICATION.KEEP]: "Keep current",
  [CLASSIFICATION.C75]: "Consider 75%",
  [CLASSIFICATION.C80]: "Consider 80%",
  [CLASSIFICATION.C85]: "Consider 85%",
  [CLASSIFICATION.INSUFFICIENT]: "Insufficient evidence to change",
  [CLASSIFICATION.ACREAGE]: "Planned acreage needed",
};

// ⚠ THE CLASSIFICATION HAS TO NAME THE LEVEL IT MEANS, AND THREE CONSTANTS CANNOT.
// It was `chosenLevel === 0.75 ? C75 : chosenLevel === 0.85 ? C85 : C80`, which was
// exhaustive while the candidate set was [0.75, 0.80, 0.85] and is a WRONG LABEL the
// moment the ladder moves: a 65% recommendation fell through to the else branch and
// was classed — and therefore rendered — as "Consider 80%".
//
// C75/C80/C85 are kept and still produced for those three levels, so nothing already
// stored or asserted changes meaning; any other level gets its own value.
export function considerClassification(level) {
  const pctv = Math.round(Number(level) * 100);
  if (pctv === 75) return CLASSIFICATION.C75;
  if (pctv === 80) return CLASSIFICATION.C80;
  if (pctv === 85) return CLASSIFICATION.C85;
  return "CONSIDER_" + pctv;
}
// One reader for both the fixed values and the generated ones, so a card never has
// to know which kind it is holding.
export function classificationLabel(c) {
  if (CLASSIFICATION_LABEL[c]) return CLASSIFICATION_LABEL[c];
  const m = /^CONSIDER_(\d+)$/.exec(String(c || ""));
  return m ? "Consider " + m[1] + "%" : "Recommended";
}

// ⚠ A BIGGER JUMP NEEDS BETTER EVIDENCE. Crossing more than one 5-point band is
// a larger commitment measured over the same thin history, so the margin
// escalates rather than staying flat. Stated, not tuned: base margin plus 0.5
// per additional band, so 1 band needs 1.25x, two 1.75x, six 3.75x.
export const MARGIN_PER_EXTRA_BAND = 0.5;
export const BAND_POINTS = 0.05;
export function bandsBetween(a, b) {
  return Math.max(1, Math.round(Math.abs(Number(a) - Number(b)) / BAND_POINTS));
}
export function marginForBands(baseMargin, bands) {
  return baseMargin + Math.max(0, bands - 1) * MARGIN_PER_EXTRA_BAND;
}

// ⚠ CAT IS A DIFFERENT PRODUCT, NOT A LOW COVERAGE LEVEL. Measured on the live
// wheat book: the ten units at coverage 0.50 with `mpci_plan_code = "01"` are
// **YP at a 0.55 price election** — catastrophic coverage — while 25 other units
// at 0.50 are genuine RP buy-up at a full price election. Recommending "move to
// 80% RP" to a CAT holder is a PRODUCT change and a deliberate election the
// engine cannot see the reason for, so it is flagged and carries the escalated
// margin rather than being quietly priced as if it were 50% RP.
export function isCatIncumbent(inc) {
  if (!inc) return false;
  const pe = inc.priceElectionPct == null ? null : Number(inc.priceElectionPct);
  return String(inc.mpciPlanCode || "") === "01" || (pe != null && pe > 0 && pe <= 0.60);
}

// Rank qualifying changes: strongest evidence first, and a TIE goes to the
// SMALLER move — the burden of proof rises with the size of the change, so
// where two are equally evidenced the engine asks for less.
export function pickBestChange(qualified) {
  const rank = (c) => (c.ratio === Infinity ? Number.MAX_SAFE_INTEGER : (c.ratio == null ? -1 : c.ratio));
  return [...qualified].sort((a, b) => (rank(b) - rank(a)) || (a.bands - b.bands))[0];
}

// One candidate, fully worked against the incumbent. Direction-aware: moving UP
// buys individual protection for a premium; moving DOWN sells it for a saving.
// ⚠ `catMove` IS EXPLICIT BECAUSE THE OLD ESCALATION WAS AN ACCIDENT OF THE LADDER.
// A move off CAT is a PRODUCT change — YP at a 0.55 price election to RP buy-up — and
// the engine cannot see why the grower elected minimum coverage in the first place, so
// it is held to a higher bar than an ordinary notch. That bar used to arrive for free:
// the candidate set was [0.75, 0.80, 0.85], so every move off a 0.50 CAT incumbent was
// five or six bands away and `marginForBands` escalated it to 3.25x. Nothing keyed on
// CAT at all. The moment the ladder became the incumbent's own neighbourhood, CAT->55%
// was ONE band and the bar silently fell back to the ordinary 1.25x.
//
// check-wheat-recommend asserted "every move off CAT carries an escalated margin" and
// passed the whole time — on distance, not on CAT. It is the test-passing-for-the-wrong-
// reason class this file keeps recording, and it was caught by the ladder change rather
// than by reading.
//
// The escalation is now one extra band's worth of the SAME stated per-band step, so it
// is explainable (1.25x -> 1.75x for a one-band move off CAT) rather than a new tuned
// constant fitted to reproduce the accident.
export function evaluateChange({ from, to, levels, need, fit, price, aph, fitAdequate, accepts, ppMaterial, catMove = false }) {
  const dir = to > from ? "up" : "down";
  const lo = Math.min(from, to), hi = Math.max(from, to);
  const bands = bandsBetween(from, to);
  const move = marginalMove(levels, lo, hi, "RP_SCO_ECO");
  const expected = bandExpectedIndemnityFor({ need, price, aph, lo, hi });
  const c = bandCase({ band: lo.toFixed(2) + "-" + hi.toFixed(2), expected, fit, move, direction: dir });

  const baseMargin = dir === "up" ? MARGIN_UP : (fitAdequate ? MARGIN_DOWN : MARGIN_DOWN_UNPROVEN);
  const required = marginForBands(baseMargin, bands + (catMove ? 1 : 0));

  // UP: value must beat cost. DOWN: saving must beat what is given up.
  const cost = move ? move.netCostPerAc : null;
  const ratio = dir === "up"
    ? c.benefitCostRatio
    : (cost != null && c.incrementalValuePerAc != null && c.incrementalValuePerAc > 0
        ? cost / c.incrementalValuePerAc
        : (cost != null && c.incrementalValuePerAc === 0 ? Infinity : null));

  const blockers = [...c.blockers];
  if (dir === "down" && !fitAdequate && !accepts)
    blockers.push(fit.usable
      ? "the county trigger has been a poor match for this farm's losses, and the grower has not accepted that basis risk"
      : "the county's record cannot be measured, so there is no evidence it would carry the band");
  if (dir === "down" && ppMaterial)
    blockers.push("prevent plant is material on this unit and the PP guarantee falls with the base level");

  const free = !!(move && move.freeOrBetter && dir === "up");
  // ⚠ A FREE MOVE RANKS AT THE TOP, NOT THE BOTTOM. `ratio` is null when the net
  // cost is <= 0 (the C > 0 guard), so a band the grower gets for nothing used to
  // sort BEHIND an ordinary 2x one — nulls coerce to 0 in the comparator. Found
  // by mutation testing, not by reading.
  const rankRatio = free ? Infinity : ratio;
  const qualifies = blockers.length === 0 && (free || (ratio != null && ratio >= required));

  const hiRow = levels.find((l) => Math.abs(l.cl - hi) < 1e-9);
  const loRow = levels.find((l) => Math.abs(l.cl - lo) < 1e-9);
  const st = (r) => r && (r.stacks.RP_SCO_ECO || r.stacks.RP_SCO || r.stacks.RP);
  const indDelta = (st(hiRow) && st(loRow))
    ? (st(hiRow).individualLiabPerAc - st(loRow).individualLiabPerAc) * (dir === "up" ? 1 : -1) : null;
  const areaDelta = (st(hiRow) && st(loRow))
    ? (st(hiRow).areaProtPerAc - st(loRow).areaProtPerAc) * (dir === "up" ? 1 : -1) : null;

  return {
    from, to, direction: dir, bands,
    // COST TO MOVE — signed from the grower's side: positive = more premium.
    netCostPerAc: cost != null ? (dir === "up" ? cost : -cost) : null,
    naiveMpciOnlyPerAc: move ? (dir === "up" ? move.naiveMpciOnlyPerAc : -move.naiveMpciOnlyPerAc) : null,
    scoDisplacedPerAc: move ? move.scoDisplaced : null,
    // PROTECTION GAIN/LOSS
    individualProtectionDeltaPerAc: r2(indDelta),
    areaProtectionDeltaPerAc: r2(areaDelta),
    // the evidence
    expectedIndemnityPerAc: c.expectedIndemnityPerAc,
    incrementalValuePerAc: c.incrementalValuePerAc,
    countyHitRate: c.countyHitRate, countyMissRate: c.countyMissRate,
    ratio: rankRatio === Infinity ? Infinity : (rankRatio != null ? Math.round(rankRatio * 100) / 100 : null),
    requiredMargin: Math.round(required * 100) / 100,
    freeOrBetter: free,
    qualifies, blockers,
  };
}

// The band expectation for an arbitrary (lo, hi], off the individual-need
// module's own series. `need` already carries the fixed bands; this reaches the
// same function for the pairs an incumbent baseline needs.
function bandExpectedIndemnityFor({ need, price, aph, lo, hi }) {
  if (!need || !need.priceable) return null;
  const key = lo.toFixed(2).replace(/^0\./, "") + "-" + hi.toFixed(2).replace(/^0\./, "");
  void key;
  return bandExpectedIndemnity({ series: need.series || [], aph, price, lo, hi });
}

// ⚠ THE PREMIUM HALF IS REMOVED, NOT MERELY UNUSED. Every net figure the ladder
// carries is nulled when the acreage cannot price one, so nothing downstream —
// a card, a PDF, a future caller — can reach a dollar computed on a nominal
// acre. The coverage half is exact at any acreage and survives untouched.
export function scrubPremium(levels) {
  for (const l of levels) {
    for (const k of Object.keys(l.stacks || {})) {
      const st = l.stacks[k];
      st.mpciNetAcre = null; st.scoNetAcre = null; st.ecoNetAcre = null; st.totalNetAcre = null;
    }
    for (const q of [l.mpci, l.sco, l.eco]) {
      if (!q) continue;
      for (const f of ["netAcre", "netTotal", "grossAcre", "grossTotal", "liabilityTotal", "coverageTotal"]) {
        if (f in q) q[f] = null;
      }
    }
  }
  return levels;
}

// ── the recommendation ───────────────────────────────────────────────────────
export function recommendWheatCoverage({
  state, county, aph, acres, price, vol, practice = "003",
  // ACREAGE PROVENANCE. Pass `acreage` — a resolveAcreageBasis result, or its
  // inputs. A bare `acres` number is still accepted and means REPORTED, which
  // is what every existing caller meant by it.
  acreage = null, plannedAcres = null, priorYearAcres = null, priorYear = null,
  rateYield = null, adjustedYield = null, priceElectionPct = 1,
  farmSeries = [], areaRows = [], quoteRP, quoteArea, unit: unitIn, areaState,
  ppAcres = null, insuredAcres = null, unitStructure = null, aphProvenance = null,
  aphHistoryAcres = null, aphHistoryYear = null,
  // RMA's own published { [cropYear]: { projected, harvest } } for this unit's
  // (state, practice, type). Given it, the band expectation becomes a revenue
  // simulation; without it the yield-only estimate stands and labels itself.
  priceHistory = null,
  farmerAcceptsBasisRisk = false,
  // ── AN AGENT'S OWN SELECTION ────────────────────────────────────────────────
  // `selection` — { level, unit, sco, eco } — is what the agent chose on screen. Any
  // field present replaces the engine's answer for that field; the rest are still the
  // engine's. It is handed to the engine rather than applied to its result so the
  // whole ladder, the stack and the next-level-up step are all derived at the level
  // actually being quoted.
  selection = null,
  // The election the grower ALREADY holds. When present it is the renewal
  // baseline and every candidate is judged as a change from it.
  // { level, plan, mpciPlanCode, priceElectionPct }
  incumbent = null,
  // How far either side of the incumbent the quote reaches. 1 = the neighbours.
  levelSpan = 1,
  scoTopPct = SCO_TRIGGER_PCT,
}) {
  const A = +aph;
  // AN EXPLICIT `acreage` OBJECT IS AUTHORITATIVE, INCLUDING ITS NULLS.
  // This read `acreage.reportedAcres != null ? acreage.reportedAcres : acres`, so a
  // caller that said in as many words "nothing is reported for the quoted year" had
  // that overridden by the NOMINAL acre figure passed alongside it for the
  // acre-invariant coverage ladder. The book runner passes 100 there, so every unit
  // in the CY2027 book resolved as priceable on 100 acres nobody reported and the
  // advisor recommended changes it should have withheld.
  //
  // It is the `: acres` else-branch default this repo has already recorded a checker
  // sailing past once - a rejected `|| 100` and an identical `: 100` one operator away.
  // A bare `acres` with NO acreage object still means REPORTED, which is what every
  // legacy caller meant by it; that path is unchanged.
  const hasBasisArg = acreage != null;
  const basis = (acreage && acreage.source)
    ? acreage
    : resolveAcreageBasis({
        reportedAcres: hasBasisArg ? acreage.reportedAcres : acres,
        plannedAcres: hasBasisArg ? acreage.plannedAcres : plannedAcres,
        priorYearAcres: hasBasisArg ? acreage.priorYearAcres : priorYearAcres,
        priorYear: hasBasisArg ? acreage.priorYear : priorYear,
        // ⚠ FORWARD EVERY RUNG. This list dropped `aphHistoryAcres` when that rung was
        // added, so the field arrived at the engine and died here — the "rule called
        // SHORT" class, and it looked exactly like the feature not working. An omitted
        // key compiles, renders, and quietly answers the wrong question.
        aphHistoryAcres: hasBasisArg ? acreage.aphHistoryAcres : aphHistoryAcres,
        aphHistoryYear: hasBasisArg ? acreage.aphHistoryYear : aphHistoryYear,
      });
  // ⚠ QUOTABLE IS NOT PRICEABLE ON THIS SURFACE. The quoter's ladder treats a prior-year
  // or assumed acreage as quotable so a quote can be PRINTED (2026-09-02); a coverage-level
  // RECOMMENDATION compares net cost between levels, and acres are the only input that moves
  // net cost while leaving coverage identical — so an estimated denominator can flip the
  // answer. Measured: the book runner's assumed 100 made 6 of 217 units recommend a change
  // they should have withheld. REPORTED and PLANNED carry no `estimated` flag; PRIOR_YEAR
  // and ASSUMED do, so a rank added to the ladder later is withheld by default.
  const priceable = !!basis.quotable && basis.estimated !== true && +basis.acres > 0;
  // ⚠ JUDGING A CHANGE AND PRINTING A PREMIUM ARE DIFFERENT QUESTIONS (2026-09-23).
  // Kevin: *"We have to quote before we will ever have acreage… The optimizer needs to
  // look at historical data not this years planted acres."* `priceable` answers the
  // premium question and must not be widened — a printed premium is a number a grower
  // signs against. `decidesCoverage` answers the other one: the farm's OWN acreage,
  // from any year, is enough to compare the marginal cost of a band against its value,
  // because both sides of that comparison move together.
  // Measured: 216 of 217 units recommend the identical level across 50-1,000 acres.
  // What is NOT enough is a nominal acre (15 of 217 differ there, all biased LOW) or an
  // assumed 100 (6 of 217 flipped) — neither is this farm's history, and both are
  // excluded by `DECIDES_COVERAGE`.
  const judgeable = decidesCoverage(basis);
  // The coverage ladder is exact at any acreage, so it is still quoted when
  // there is nothing real to quote at — at a NOMINAL acre, with the premium
  // scrubbed below rather than shown at that acre.
  // The acreage the ladder is PRICED at. `judgeable` means this farm has a real
  // figure of its own somewhere; only then is a cost comparison meaningful.
  const AC = judgeable ? +basis.acres : NOMINAL_QUOTE_ACRES;
  if (!(A > 0) || !(+price > 0) || typeof quoteRP !== "function" || typeof quoteArea !== "function") return null;
  const CO = String(county || "").toUpperCase();
  // ⚠ THE UNIT STRUCTURE IS THE POLICY'S, NOT A GUESS OFF ACREAGE. Measured on
  // one real unit, the BU->EU flip at 20 acres moves the 75->80 cost 27% while
  // the whole discount band from 100 to 1,000 acres moves it 3% — so deriving
  // the structure from an invented acreage is the larger of the two errors.
  const asUnit = (v) => { const x = String(v || "").toUpperCase(); return UNIT_CODES.includes(x) ? x : null; };
  const storedUnit = String(unitStructure || "").toUpperCase();
  const unitFromPolicy = UNIT_CODES.includes(storedUnit) ? storedUnit : null;
  // ⚠ `selection.unit` IS THE SAME STATEMENT ARRIVING BY THE OTHER DOOR, and the
  // selection contract above has promised it since the day it was written while nothing
  // read it — so an agent's structure could be handed to this engine and silently
  // ignored. It is folded into the caller's unit rather than given a precedence of its
  // own, because two rules for which structure wins is how the card and the printed
  // sheet came to quote one farm at two different premiums. Both doors are VALIDATED
  // against the code list: an unrecognised string used to pass straight through into
  // `quoteRP`, where it rates as nothing in particular.
  const unitCaller = asUnit(unitIn) || (selection ? asUnit(selection.unit) : null);
  const unit = unitCaller || unitFromPolicy || (priceable ? (AC >= 20 ? "EU" : "BU") : "BU");
  const unitSource = unitCaller ? "caller" : unitFromPolicy ? "policy"
                   : priceable ? "derived_from_acres" : "nominal";
  const aSt = postalStateOf(areaState || state);

  // The incumbent level is priced ALONGSIDE the candidates, because a renewal is
  // compared against what the grower actually holds. A level RMA does not rate
  // for this county drops out of the set rather than being approximated.
  const incLevel = incumbent && incumbent.level != null && Number(incumbent.level) > 0
    ? Number(incumbent.level) : null;
  // ⚠ THE CANDIDATE SET IS THE INCUMBENT AND ITS NEIGHBOURS, NOT A FIXED THREE.
  // It was `[0.75, 0.80, 0.85] ∪ incumbent`, which showed a 60% farm a jump to 85%
  // and never showed it 65%. RMA's ladder is wildly non-linear — on NY GENESEE the
  // 60→65 step is 93 cents an acre and the 75→80 step is $5.48 — so the steps a
  // grower would actually weigh are the adjacent ones, and a fixed high set showed
  // the wrong conversation to the 266 of 336 base RP wheat coverages below 80%.
  //
  // `levelSpan` widens it; the optimizer prices the WHOLE ladder through its own
  // entry point rather than by widening this one, because "what should this renewal
  // show" and "what would every level have done" are different questions.
  const ladderSet = candidateLevels(
    incLevel != null || incumbent ? { ...(incumbent || {}), level: incLevel } : null,
    { span: levelSpan }
  );
  // ⚠ ONE RUNG ABOVE THE LADDER IS PRICED FOR DISPLAY AND IS NOT A CANDIDATE.
  // `nextLevelUp` answers "what would one more step cost" and needs that level in
  // the priced stack. It is added to `wanted` (what gets PRICED) and deliberately
  // NOT to `ladderSet.levels` (what can be RECOMMENDED) — the band walk and both
  // `changes` loops read the ladder, so a level that exists only to be quoted can
  // never be chosen. Without this the field went null at the top of every renewal
  // ladder, which reads as "there is nothing above this" rather than "we did not
  // put it in the set".
  const topRung = ladderSet.levels[ladderSet.levels.length - 1];
  const displayOnly = WHEAT_LEVELS.filter((L) => L > topRung + 1e-9 && L < topRung + 0.05 + 1e-9);
  // ⚠ THE AGENT'S OWN PICK MUST BE PRICED, OR THE BRANCH BELOW CANNOT SEE IT (2026-09-23).
  // The agent branch is gated on `levels.some(l => l.cl === selection.level)` — a level that
  // was never quoted fails that test, so the selection was DISCARDED IN SILENCE and the sheet
  // printed the engine's own answer under the engine's own heading. The gate is right (a level
  // the county does not rate must not be quoted); what was wrong is that `wanted` was built
  // from the RECOMMENDATION ladder alone, which is narrow and centred on the incumbent.
  //
  // HARTWAY is the case. His wheat is elected at 50%, so `candidateLevels` returns
  // [50, 55, 60]; the agent picked 80% on the card, the card re-priced and said "and that is
  // what prints for Genesee", and both the printed quote and the Full Strategy Report carried
  // 50% under "2027 Recommended".
  //
  // ⚠ IT WAS THE NORMAL CASE, NOT AN EDGE, AND IT IS NOT ONLY UPWARD PICKS. Measured
  // 2026-09-23 by replaying the SHIPPED `candidateLevels` over all **231 wheat units**
  // (farmer x county x practice, the CY2027 row else CY2026) against every level an agent
  // could pick: **991 of 1,617 (unit x level) picks — 61.3% — were silently discarded.**
  // The ladder is centred on the incumbent, so the 132 units already at 75% could not be
  // moved DOWN to 50/55/60/65% either, which is how a grower gets moved to CAT.
  //
  //   incumbent   units   priced ladder        picks that vanished
  //      50%        23     50,55,60            65 70 75 80 85
  //      60%         8     55,60,65,70         50 75 80 85
  //      65%         6     60,65,70,75         50 55 80 85
  //      70%        18     65,70,75,80         50 55 60 85
  //      75%       132     70,75,80,85         50 55 60 65
  //      80%        44     75,80,85            50 55 60 65 70
  //
  // It joins `wanted` (what gets PRICED) and NOT `ladderSet.levels` (what can be RECOMMENDED),
  // exactly as `displayOnly` above does, so pricing a level can never make the engine choose
  // it. Filtered to RMA's own published set so a typo cannot ask the rate tables for 0.99.
  const selLevel = selection && selection.level != null
    && WHEAT_LEVELS.some((L) => Math.abs(L - +selection.level) < 1e-9) ? +selection.level : null;
  const wanted = [...new Set([...ladderSet.levels, ...displayOnly,
                              ...(incLevel != null ? [incLevel] : []), ...(selLevel != null ? [selLevel] : [])])]
    .sort((a, b) => a - b);
  const levelsRaw = buildLevels({
    state, county: CO, aph: A, acres: AC, price, vol, practice, unit,
    rateYield, adjustedYield, priceElectionPct, quoteRP, quoteArea, scoTopPct,
    levels: wanted,
  });
  // ⚠ THE DECISION LADDER IS NOT SCRUBBED. THE RETURNED ONE IS (2026-09-23).
  // This line used to read `priceable ? levelsRaw : scrubPremium(levelsRaw)`, and
  // `scrubPremium` MUTATES — so on any estimated acreage every net figure was nulled
  // before the margin test ever ran, and `bandCase` reported "the marginal cost could
  // not be priced" on every candidate move. Measured 2026-09-23 over the CY2027 wheat
  // book: that was the SINGLE BIGGEST blocker, 179 occurrences — more than the county
  // miss rate (177) and the band expectation (91).
  //
  // A change is judged on cost AND value, and both are now computed at the farm's own
  // acreage (`judgeable`). Withholding the PREMIUM is a display rule, so the scrub moves
  // to just before the return, where it still nulls every dollar a card, a PDF or a
  // future caller could reach — `scrubPremium`'s own contract, unchanged.
  const levels = levelsRaw;
  if (!levels.length) return null;
  const incPriced = incLevel != null && levels.some((l) => Math.abs(l.cl - incLevel) < 1e-9);

  // A — the farm, blind to the county.
  // ⚠ THE SETTLEMENT RULE IS THE POLICY'S, NEVER ASSUMED. `revenuePlanOf` reads the
  // incumbent's own plan code; where it cannot establish one it returns null and the
  // band expectation falls back to the yield basis rather than assuming RP — RP is the
  // most generous of the three, so assuming it would overstate what coverage has been
  // worth and bias every recommendation upward.
  const revPlan = revenuePlanOf({ plan: incumbent && incumbent.plan,
                                  mpciPlanCode: incumbent && incumbent.mpciPlanCode });
  const need = individualNeed({ series: farmSeries, aph: A, price,
                                ppAcres, insuredAcres, unitStructure, aphProvenance,
                                priceHistory, revenuePlan: revPlan });
  // B — the county, separately.
  const fit = aSt
    ? areaFit({ farmSeries, areaRows, state: aSt, county: CO, aph: A })
    : { ...areaFit({ farmSeries, areaRows, state: "??", county: CO, aph: A }),
        reasons: ['state "' + state + '" has no postal mapping, so no county history can be read'] };

  // C — what each move costs, on the full stack.
  const up = marginalMove(levels, 0.80, 0.85, "RP_SCO_ECO");
  const down = marginalMove(levels, 0.75, 0.80, "RP_SCO_ECO");

  const caseUp = bandCase({ band: "80-85", expected: need.bands["80-85"], fit, move: up, direction: "up" });
  const caseDown = bandCase({ band: "75-80", expected: need.bands["75-80"], fit, move: down, direction: "down" });

  // ── WHY NOT 85% / WHY NOT 75% — THE SENTENCES COME FROM THE BAND WALK ──────
  //
  // ⚠ A DEAD DECISION RULE STOOD HERE UNTIL 2026-09-22, AND IT READ AS THE
  // SHIPPED ONE. `upFree` / `upEarned` / `recommend85` computed an 85% verdict
  // out of `caseUp`, under a comment stating the rule ("recommended only on
  // affirmative economics…"), and NOTHING CONSUMED ANY OF IT: the returned
  // `whyNot85` is `bandSentence(0.80)` and the level is `chooseBandLevel`'s.
  // Same for the local `whyNot75`. Both were left behind when the band walk
  // superseded the bandCase path, and the comment kept describing the retired
  // architecture.
  //
  // It was found by mutation testing, not by reading: the mutation that
  // restores a GRADE route to 85% — the exact defect the second pass removed —
  // was the one survivor of 49, because it mutated a variable with no reader.
  // **A rule nothing calls cannot be tested, and a mutation that cannot fail is
  // indistinguishable from a rule that cannot break.**
  //
  // `caseUp` / `caseDown` STAY — they are returned as `cases` and `tools/
  // wheat-book-run.js` prints `cases.up.benefitCostRatio` against MARGIN_UP.
  // MARGIN_UP stays too; it is live at `evaluateChange`'s `baseMargin`.

  // ── WHAT A DROP HAS TO CLEAR ───────────────────────────────────────────────
  // Dropping to 75% hands another 5 points to the county trigger. It needs the
  // saving to clearly exceed what is given up, an area fit good enough to carry
  // the band, and no material prevent-plant consequence.
  const savedDown = down ? down.netCostPerAc : null;      // what 75% saves
  const forgone = caseDown.incrementalValuePerAc;          // what it gives up
  // "Area fit is adequate OR the farmer explicitly accepts the basis risk."
  // Adequate means MEASURED and not poor. A WEAK fit is measured evidence
  // AGAINST handing the county another five points, and an unmeasurable one is
  // no evidence at all — neither is adequate, and the difference between them is
  // stated rather than collapsed.
  // ⚠ `usable` IS NOT `MEASURED`, AND THAT GAP DECIDED EVERY DOWNWARD MOVE IN
  // THE BOOK. `areaFit` sets `usable = r != null` — a CORRELATION could be
  // computed — and its r-only branch lands on GOOD by construction, under its own
  // comment: "a general relationship is not a measured downside". So a fit that
  // has never been tested against a single farm loss passed `fit !== WEAK` and
  // carried a drop, while the same object's `reasons` array said "0 farm loss
  // years on record (need 3 to rate the downside)". The engine knew; the guard
  // did not read it.
  //
  // Measured over the CY2027 book at a planned 180 ac: of the 31 units this
  // permitted a DROP for, **0 had a measured hit rate, 0 had a priceable forgone
  // value, 0 had a ratio, and all 31 had fewer than MIN_LOSS_YEARS loss years**
  // — against 12 of the 20 UPWARD moves that did. The evidence bar was inverted
  // exactly where handing five points to the county needs it most.
  //
  // `hitRate != null` IS the measured downside, on the same threshold areaFit
  // already refuses below. An r-only fit is not refused — it is UNPROVEN, which
  // is the state `MARGIN_DOWN_UNPROVEN` and `downUncertain` already exist for.
  const fitAdequate = fit.usable && fit.fit !== FIT.WEAK && fit.hitRate != null;
  const accepts = !!farmerAcceptsBasisRisk;
  const fitAllowsDown = fitAdequate || accepts;
  // Where the fit does NOT carry the case and the farmer has accepted the risk
  // anyway, the economics must clear a stricter bar — the saving is being taken
  // against an unproven substitute.
  const marginDown = fitAdequate ? MARGIN_DOWN : MARGIN_DOWN_UNPROVEN;
  const downRatio = (savedDown != null && forgone != null && forgone > 0)
    ? savedDown / forgone : (savedDown != null && forgone === 0 ? Infinity : null);
  const ppMaterial = need.ppFrac != null && need.ppFrac >= PP_MATERIAL_FRAC;
  const downEarned = downRatio != null && downRatio >= marginDown && !ppMaterial && fitAllowsDown;
  const recommend75 = !!(down && downEarned);
  // `recommend75` is LIVE — it is what makes `downUncertain` mean "we are
  // recommending a drop that the area fit does not carry", which is returned and
  // printed. It is deliberately not folded into the band walk.
  const downUncertain = recommend75 && !fitAdequate;

  // ── THE CALL ───────────────────────────────────────────────────────────────
  //
  // Two paths, and which one runs depends on whether there IS an incumbent.
  //
  //   RENEWAL      the incumbent is the baseline. Every candidate is evaluated
  //                as a CHANGE from it, and only an affirmatively evidenced
  //                change is recommended. No evidence -> no change.
  //   NEW BUSINESS 80% is the initial benchmark and 75/80/85 are evaluated from
  //                there, which is the previous behaviour, unchanged.
  const cat = isCatIncumbent(incumbent);
  const changes = [];
  let classification = null, keepReason = null, chosenLevel = null, conflict = null;

  // ── the farm's own record, and RMA's own price history ────────────────────
  const profile = yieldProfile(farmSeries, A);
  const priceRatios = priceRatiosFrom(areaRows);
  let bandResult = null;

  // ⚠ AN AGENT'S PICK IS A STATEMENT, NOT A RECOMMENDATION — SO IT IS TESTED FIRST
  // (2026-09-23). This used to sit BEHIND the `!judgeable` branch, so a farm with no
  // acreage on record anywhere had the agent's own selection silently swallowed by the
  // acreage refusal and printed the engine's held incumbent instead. Nothing about a pick
  // needs a cost denominator: the agent is not asking to be judged, and the PREMIUM on an
  // unpriceable unit is withheld further down by `scrubPremium` either way. The refusal is
  // about recommending a CHANGE, which is a different act from quoting what was chosen.
  const agentPicked = selection && selection.level != null
    && levels.some((l) => Math.abs(l.cl - +selection.level) < 1e-9);
  // A level the agent asked for that the county's rate tables cannot price. REPORTED rather
  // than swallowed: the whole defect this ordering fixes was a pick vanishing in silence, and
  // falling back to the engine's answer with no signal reproduces it one step further along.
  const selectionRefused = !!(selection && selection.level != null && !agentPicked)
    ? { level: +selection.level,
        reason: WHEAT_LEVELS.some((L) => Math.abs(L - +selection.level) < 1e-9)
          ? "this county's rate tables returned no quote at " + Math.round(+selection.level * 100) + "%"
          : Math.round(+selection.level * 100) + "% is not an RMA coverage level" }
    : null;

  if (agentPicked) {
    // ── THE AGENT PICKED IT ──────────────────────────────────────────────────
    // The band walk is not run. Its output would be a recommendation nobody is making,
    // and reporting engine reasoning beside a level a human chose is the contradiction
    // this branch exists to avoid.
    //
    // `changes` is still built, because the card and the sheet render the ladder of
    // alternatives around whatever level is being quoted.
    const selBase = incLevel != null && incPriced ? incLevel : ANCHOR_LEVEL;
    for (const cand of ladderSet.levels) {
      if (Math.abs(cand - selBase) < 1e-9) continue;
      if (!levels.some((l) => Math.abs(l.cl - cand) < 1e-9)) continue;
      changes.push(evaluateChange({ from: selBase, to: cand, levels, need, fit,
                                    price, aph: A, fitAdequate, accepts, ppMaterial, catMove: cat }));
    }
    chosenLevel = +selection.level;
    classification = CLASSIFICATION.AGENT;
    keepReason = "selected by the agent" + (incLevel != null
      ? " (the current election is " + Math.round(incLevel * 100) + "%)" : "");
  } else if (!judgeable) {
    // ── NO ACREAGE OF THIS FARM'S OWN, IN ANY YEAR ───────────────────────────
    // Every candidate move is judged on net cost per acre, and there is no
    // acreage to compute one from. The coverage ladder above is exact and is
    // returned; nothing here compares anything, so no change can be earned by
    // an invented denominator. The incumbent is held because that is what the
    // grower has, NOT because it won a comparison.
    // ⚠ This used to test `priceable`, which excluded the farm's own prior-year
    // and APH-history acreage and so fired on the ENTIRE CY2027 book. It now
    // fires only where the farm has no wheat acreage on record anywhere —
    // measured 2026-09-23: 22 of 214 units, and every one of those is new
    // ground. Those get an honest refusal plus an invitation to type a figure.
    chosenLevel = incLevel != null ? incLevel : ANCHOR_LEVEL;
    classification = CLASSIFICATION.ACREAGE;
    keepReason = basis.detail + " — the premium comparison a change is judged on cannot be computed";
  } else {
    // ── THE BAND WALK ────────────────────────────────────────────────────────
    //
    // One rule for renewal AND new business. The old engine ran two paths — an
    // incumbent-baselined renewal and an 80%-anchored new-business branch — and the
    // split is no longer needed: a band is owned on its own evidence, and the incumbent
    // enters only where that evidence cannot decide (BORDERLINE) or does not exist.
    //
    // `changes` is still populated, because the card and the printed sheet render the
    // full ladder of alternatives beside the recommendation. Nothing below reads
    // `qualifies` to PICK a level — but the down-move guard added after the walk reads
    // it to REFUSE one, so this list is no longer purely descriptive in that one
    // direction. See the block beneath `chooseBandLevel`.
    const baseline = incLevel != null && incPriced ? incLevel : null;
    for (const cand of ladderSet.levels) {
      if (baseline != null && Math.abs(cand - baseline) < 1e-9) continue;
      if (!levels.some((l) => Math.abs(l.cl - cand) < 1e-9)) continue;
      changes.push(evaluateChange({ from: baseline != null ? baseline : ANCHOR_LEVEL, to: cand,
                                    levels, need, fit, price, aph: A, fitAdequate, accepts, ppMaterial, catMove: cat }));
    }

    // The SHIPPED netting rule, not a second copy — marginalMove already subtracts the
    // SCO premium a buy-up displaces, and it is guarded in coverageStack.js.
    bandResult = chooseBandLevel({ profile, aph: A, price, incumbent: baseline,
                                   priceRatios, ppMaterial, ladder: ladderSet.levels,
                                   marginal: (from, to) => marginalMove(levels, from, to) });
    chosenLevel = bandResult.level;

    // ── ⚠ A DROP BELOW THE INCUMBENT IS EARNED BY THE SAME TEST AN EXPLICIT DROP IS ──
    //
    // The band walk starts at the BOTTOM of the ladder and owns each band that earns
    // its cost, so it can arrive BELOW the grower's own election by never owning a band
    // rather than by deciding to sell one. That route reaches a lower level without the
    // three checks `evaluateChange` applies to a deliberate DOWN move — an adequate
    // area fit or the grower's explicit acceptance of the basis risk, the prevent-plant
    // block, and the escalated margin an unproven fit carries.
    //
    // THE HARD FLOOR AT 75% WAS DOING A GUARD'S JOB, AND NOBODY NOTICED. While the
    // ladder was the fixed [0.75, 0.80, 0.85] a 75% grower could not be walked down at
    // all, so the bypass was only reachable from 80% — measured on the CY2027 book at a
    // uniform 180 planned acres, 13 of 217 units. Building the ladder around the
    // incumbent removed the floor and the same route took it to 48 units and 8,640
    // acres: 75->70 on 22, 70->65 on 5, 60->55 on 5. The defect is older than the
    // ladder change; the ladder change is what made it visible.
    //
    // This repo already states the rule in one direction — "a lack of evidence is not
    // evidence to buy more coverage". The mirror is what is enforced here: a lack of
    // evidence is not evidence to carry LESS, and of the two errors this is the one
    // that costs a grower a claim.
    //
    // `changes` stops being purely descriptive for exactly this case, which contradicts
    // the comment twenty lines above; that is deliberate and the comment is corrected.
    // Nothing reads `qualifies` to CHOOSE a level — it is read to REFUSE one.
    // ⚠ THE GUARD IS `fitAllowsDown && !ppMaterial`, NOT the full `qualifies`.
    // The first cut demanded the whole `evaluateChange` verdict and was too blunt: it
    // double-counts the individual evidence. `chooseBandLevel` only ever drops a band on
    // EVID.NOT — every leave-one-year-out sample agreeing the band does not cover its
    // cost — while BORDERLINE already defers to the incumbent. So the individual half is
    // affirmatively evidenced before this line is reached, and re-testing it through
    // `bandCase`'s ratio refused every farm whose county MISS RATE is unmeasured, which
    // is 167 of 343 units. That would have held roughly half the book at its incumbent
    // for a reason the band walk had already answered.
    //
    // What the walk genuinely does NOT ask is the AREA half, and that is the real gap:
    // dropping 80 to 75 moves those five points off the farm's own trigger and onto the
    // COUNTY's, because SCO backfills the band. That is only safe if the county is an
    // adequate substitute — or the grower has explicitly accepted the basis risk. It is
    // the same `fitAllowsDown` the explicit-drop path has always used, and the same
    // prevent-plant block, because the PP guarantee is a fraction OF the base level.
    let downRefused = null;
    if (baseline != null && chosenLevel < baseline - 1e-9 && (!fitAllowsDown || ppMaterial)) {
      const drop = changes.find((c) => Math.abs(c.to - chosenLevel) < 1e-9 && c.direction === "down");
      // ⚠ THREE REASONS, NOT TWO. `fit.usable` only says a CORRELATION exists, so
      // the old `usable ? "poor match" : "cannot be measured"` split reported an
      // r-only fit as "a poor match for this farm's losses" — a claim about losses
      // that were never matched against anything. Measured on the CY2027 book, that
      // was EVERY refusal: 0 of the affected units carry a measured hit rate.
      const measuredPoor = fit.hitRate != null;
      const neverTested = !measuredPoor && fit.usable;
      downRefused = {
        to: chosenLevel, from: baseline,
        // Whether the county was ever TESTED, which is what decides the
        // classification below — a refusal we could not evaluate is not a finding
        // about the farm.
        unmeasured: !ppMaterial && !measuredPoor,
        // The number that actually explains an unmeasured refusal, carried so the
        // customer report does not have to say "11 years is not enough" about a
        // shortage of LOSS years.
        lossYears: fit.nLossYears != null ? fit.nLossYears : null,
        reason: ppMaterial
          ? "prevent plant is material on this unit and the PP guarantee falls with the base level"
          : measuredPoor
            ? "the county trigger has been a poor match for this farm's losses, so handing it another "
              + Math.round((baseline - chosenLevel) * 100) + " points is not supported without the grower accepting that basis risk"
            : neverTested
              ? "this farm has " + (fit.nLossYears || 0) + " loss year"
                + ((fit.nLossYears || 0) === 1 ? "" : "s") + " on record, so the county trigger has never been "
                + "tested against a loss here — there is no evidence either way that it would carry the "
                + Math.round((baseline - chosenLevel) * 100) + " points being given up"
              : "the county's record cannot be measured here, so there is no evidence the area trigger would carry the band being given up",
        blockers: drop ? drop.blockers : [],
      };
      chosenLevel = baseline;
    }
    bandResult.downRefused = downRefused;

    // ⚠ EVIDENCE SUFFICIENCY IS WHAT SEPARATES "we compared and the incumbent won" FROM
    // "we could not compare". Both leave the election alone; only one of them is a
    // finding, and collapsing them is how a book of unmeasurable farms reads as a book
    // of confirmed-correct ones.
    // A drop the walk WANTED to make and was refused because the county was never
    // tested is the same fact as a short history: we could not compare. Without
    // this the unit reads KEEP CURRENT — "we compared and your election won" —
    // which is the one thing that did not happen.
    if (bandResult.shortHistory || bandResult.belowRange
        || (downRefused && downRefused.unmeasured)) {
      classification = CLASSIFICATION.INSUFFICIENT;
      keepReason = (downRefused && downRefused.unmeasured) ? downRefused.reason : bandResult.why;
    } else if (baseline != null && Math.abs(chosenLevel - baseline) < 1e-9) {
      classification = CLASSIFICATION.KEEP;
      // A level HELD because the drop was refused is a different finding from one the
      // walk landed on by itself, and it says which — otherwise the card reports "keep
      // current" with a reason about a band the grower is not being offered.
      keepReason = downRefused
        ? "this farm's own record does not carry the " + Math.round(downRefused.to * 100) + "–"
          + Math.round(downRefused.from * 100) + "% band, but it is held anyway: " + downRefused.reason
        : bandResult.why;
    } else {
      classification = considerClassification(chosenLevel);
    }
    if (incLevel != null && !incPriced)
      keepReason = "the current level (" + Math.round(incLevel * 100) +
        "%) is not rated for this county and practice, so it could not be used as the baseline";
  }

  let cl = chosenLevel;
  let mode = incLevel != null
    ? (Math.abs(cl - incLevel) < 1e-9 ? REC.ANCHOR : (cl > incLevel ? REC.UP : REC.DOWN))
    : (cl === 0.85 ? REC.UP : cl === 0.75 ? REC.DOWN : REC.ANCHOR);

  const chosen = levels.find((l) => Math.abs(l.cl - cl) < 1e-9) || levels.find((l) => Math.abs(l.cl - ANCHOR_LEVEL) < 1e-9) || levels[0];
  if (Math.abs(chosen.cl - cl) > 1e-9) cl = chosen.cl;

  // The 80% benchmark is ALWAYS computed and shown, whatever the call — it is
  // what the alternatives are measured against, not a destination.
  const benchmarkRow = levels.find((l) => Math.abs(l.cl - ANCHOR_LEVEL) < 1e-9) || null;
  // ⚠ THIS BLOCK READS THE LADDER BEFORE THE SCRUB, SO IT WITHHOLDS ITS OWN PREMIUM.
  // The scrub used to run up at line ~519 and everything below it inherited null;
  // it now runs immediately before the return so the decision can see real costs,
  // which means every figure CAPTURED between here and there has to withhold for
  // itself. `totalNetAcre` is a premium and obeys `priceable`; the coverage figure
  // beside it is exact at any acreage and does not.
  const benchmark = benchmarkRow ? {
    cl: ANCHOR_LEVEL,
    totalNetAcre: priceable ? (benchmarkRow.stacks.RP_SCO_ECO || benchmarkRow.stacks.RP).totalNetAcre : null,
    individualLiabPerAc: (benchmarkRow.stacks.RP_SCO_ECO || benchmarkRow.stacks.RP).individualLiabPerAc,
    isIncumbent: incLevel != null && Math.abs(incLevel - ANCHOR_LEVEL) < 1e-9,
    isRecommended: Math.abs(cl - ANCHOR_LEVEL) < 1e-9,
  } : null;

  // ── SCO and ECO, judged on economics AND fit, separately ───────────────────
  const scoEcon = gradeEconomics(chosen.sco ? chosen.sco.covPerAc : null, chosen.sco ? chosen.sco.netAcre : null);
  const ecoEcon = gradeEconomics(chosen.eco ? chosen.eco.covPerAc : null, chosen.eco ? chosen.eco.netAcre : null);
  // ⚠ SCO AND ECO ARE THE FROZEN DEFAULT STRUCTURE (Kevin, 2026-09-03), so an offered
  // endorsement is recommended. The economics grade and the area fit BELOW are retained
  // and still rendered as evidence — they simply no longer gate the recommendation.
  //
  // The measurement behind the change: at the CY2027 80% subsidy the producer pays ~20%
  // of gross, so the break-even gross loss ratio is 0.20, and these layers return a
  // median 0.42 (ECO) and 0.27 (SCO) on RMA's own settled county record — well clear.
  // The old gate never fired anyway (0 of 262 quotes graded POOR), and the county
  // frequency and farm-fit signals that might have replaced it were measured and
  // REFUSED: county frequency does not track RMA's own area rate (r = -0.417 on ECO
  // across 32 counties), and farm fit carries no information about a layer's expected
  // value (identical 2.09x median return with and without a negative fit).
  // An agent's tick can only ever turn OFF an endorsement the county offers; it cannot
  // conjure one the county does not sell, which is why the offer test still gates both.
  const recSco = !!chosen.sco && !(selection && selection.sco === false);
  const recEco = !!chosen.eco && !(selection && selection.eco === false);
  const stackKey = recSco && recEco ? "RP_SCO_ECO" : recSco ? "RP_SCO" : recEco ? "RP_ECO" : "RP";
  const stack = chosen.stacks[stackKey] || chosen.stacks.RP;

  // ⚠ AREA COVERAGE IS NOT INDIVIDUAL COVERAGE, and the page must not let the
  // arithmetic imply it. 75 RP + SCO + ECO95 is NOT 95% individual coverage.
  const topPct = chosen.ecoBand ? 95 : chosen.scoBand ? scoTopPct : Math.round(cl * 100);
  const basisRisk = {
    individualTopPct: Math.round(cl * 100),
    areaTopPct: topPct,
    gapPoints: topPct - Math.round(cl * 100),
    note: "Your own trigger covers to " + Math.round(cl * 100) + "%. The " +
      (topPct - Math.round(cl * 100)) + " points above that are COUNTY-triggered: they can pay when you had no loss, " +
      "and fail to pay when you did. This is not " + topPct + "% individual coverage.",
    timingNote: "Area endorsements settle on county results and pay months after an individual claim.",
    ppNote: need.ppFrac != null
      ? "Prevent plant on this unit was " + pct(need.ppFrac) + " of insured acres; the PP guarantee moves with the base level."
      : "Prevent-plant exposure is not measurable for this unit, so its effect on lowering the base level is unknown.",
  };

  // ── the sentences the card and the printed sheet render ───────────────────
  //
  // Same five lines in the same order as before — only the reasoning behind them
  // changed. Each band's answer is stated in the grower's own terms: what the band
  // costs, and what it would have collected on their own record.
  const bandAt = (from) => (bandResult && bandResult.bands || []).find((b) => Math.abs(b.from - from) < 1e-9);
  const bandSentence = (from) => {
    const b = bandAt(from);
    const name = Math.round(from * 100) + "–" + Math.round((from + 0.05) * 100);
    if (!bandResult) return "the premium comparison it is judged on cannot be computed without an acreage";
    if (bandResult.belowRange) return "the current election sits below the 75–85% range this comparison covers";
    if (bandResult.shortHistory) return "there is not enough yield history to test the " + name + " band";
    if (!b) return "the " + name + " band was not reached — the band below it did not clear its own cost";
    if (!b.priceable) return "the " + name + " band is not rated for this county and practice";
    const econ = "$" + b.expIndemnity.toFixed(2) + "/ac of historical value against $"
      + b.margPrem.toFixed(2) + "/ac of premium, over " + b.nYears + " years";
    // ⚠ THE MARGIN CASE IS NOT THE "did not cover its cost" CASE. It covered it;
    // it did not clear the bar the top level is held to. Printing the wrong one
    // of those beside the band's own ratio is a card contradicting itself.
    if (b.marginShort)
      return "the " + name + " band covers its own cost (" + econ + ", " + b.ratio.toFixed(2)
        + "x) but not by the " + BAND_MARGIN_TOP + "x margin the top level is held to, and it "
        + "falls to " + b.looMin.toFixed(2) + "x if a single year is dropped — the subsidy is "
        + "lowest on these five points, so the current election stands";
    if (b.evidence === EVID.SUPPORTS) return "the " + name + " band clears its own cost — " + econ;
    if (b.evidence === EVID.NOT) return "the " + name + " band has not covered its cost — " + econ;
    return "the " + name + " band is too close to call — " + econ
      + ", and the answer reverses if a single year is dropped, so the current election stands";
  };
  const headline = bandResult && !bandResult.held && bandResult.decidedBy === "ALL_SUPPORTED"
    ? "every buy-up band clears its own cost on this farm's record."
    : (bandResult ? bandResult.why + "." : "the acreage needed to price a comparison is missing.");
  const why = [
    "Recommended " + Math.round(cl * 100) + "% RP" + (recSco ? " + SCO" : "") + (recEco ? " + ECO95" : "") +
      " — " + headline,
    "Why not 85%: " + (Math.abs(cl - 0.85) < 1e-9 ? "it is the recommendation." : bandSentence(0.80) + "."),
    "Why not 75%: " + (Math.abs(cl - 0.75) < 1e-9 ? "it is the recommendation." : bandSentence(0.75) + "."),
    individualNeedSentence(need),
    areaFitSentence(fit, aSt),
  ];

  // ⚠ SCRUB HERE, AFTER THE DECISION AND BEFORE ANYTHING IS HANDED OUT. Every net
  // figure the ladder carries is nulled when the acreage cannot price one, so nothing
  // downstream can reach a dollar computed on a figure nobody reported — including the
  // derived `nextLevelUp` / `moveFromIncumbent` costs built in the object below, which
  // are computed from `levels` and therefore inherit the scrub. The decision above
  // already ran on the real numbers, which is the whole point of moving this line.
  if (!priceable) scrubPremium(levels);

  return {
    cl, mode, unit, stackKey,
    // ── the renewal decision ────────────────────────────────────────────
    classification, classificationLabel: CLASSIFICATION_LABEL[classification],
    baseline: (incLevel != null && incPriced) ? "incumbent" : "benchmark80",
    incumbentLevel: incLevel, incumbentPriced: incPriced,
    incumbentIsCat: cat,
    isChange: incLevel != null && Math.abs(cl - incLevel) > 1e-9,
    changes, keepReason,
    // What the agent asked for and this engine could not quote. Null on every ordinary
    // result, and asserted both ways by check-wheat-recommend.
    //
    // ⚠ NO SURFACE RENDERS IT YET, AND THAT IS STATED RATHER THAN LEFT TO BE DISCOVERED.
    // It is reachable only when a pick is a real RMA level that the county's own tables
    // return no quote for — the card offers nothing else, so it cannot fire from the level
    // chips today. When it does fire the document is not lying (the engine's own answer is
    // returned and the header correctly reads "2027 Recommended", not "Selected"), but the
    // agent's card and the sheet then disagree with nothing saying why. The right home is
    // the Full Strategy Report's draft banner, whose `add()` reads a per-county VIEW object
    // rather than this result, so it needs `uRec.selectionRefused` threaded through the
    // view-model builder in App.jsx (~line 51477, where `uRec` is already in scope).
    // Deliberately not half-plumbed at the end of this fix.
    selectionRefused,
    benchmark,
    evidenceSufficient: classification !== CLASSIFICATION.INSUFFICIENT && classification !== CLASSIFICATION.ACREAGE,
    // ── acreage provenance ──────────────────────────────────────────────────
    // Stated on every result, so a reader never has to infer where the number
    // under a premium came from.
    acreage: {
      acres: priceable ? +basis.acres : null,
      source: basis.source, label: basis.label, detail: basis.detail,
      quotable: priceable, reference: basis.reference,
      quotedAt: priceable ? +basis.acres : NOMINAL_QUOTE_ACRES,
      unlockedByAcreage: priceable ? [] : UNLOCKED_BY_ACREAGE,
    },
    premiumWithheld: !priceable,
    unitSource,
    mpci: chosen.mpci, sco: recSco ? chosen.sco : null, eco: recEco ? chosen.eco : null,
    scoOffered: chosen.scoOffered, ecoOffered: chosen.ecoOffered,
    netAcre: stack ? stack.totalNetAcre : null,
    bands: { individual: chosen.individualBand, sco: chosen.scoBand, eco: chosen.ecoBand },
    // A, B and C, each addressable on its own
    need, fit, levels,
    moves: { up, down },
    // ⚠ THE STEP FROM THE RECOMMENDATION, not from the 80% anchor. `moves.up` is
    // fixed at 80->85 and several surfaces read it as "the next level up"; that is
    // only the same thing when the recommendation IS 80%. Null at the top of the
    // ladder, because there is no next level to price.
    nextLevelUp: cl < 0.85 - 1e-9 ? marginalMove(levels, cl, cl + 0.05, stackKey) : null,
    // ⚠ THE MOVE THIS RECOMMENDATION ACTUALLY IS, priced from the INCUMBENT.
    // `moves.up` is the fixed 80->85 pair and `nextLevelUp` is the step above the
    // recommendation; NEITHER is the change being recommended when the
    // recommendation is itself a change. The Full Strategy Report read `moves.up`
    // as its trade-off and, on a farm recommended UP to 85%, printed "85% would
    // cost another $14.73/ac" directly under "we recommend 85%" — the cost of the
    // recommendation, under a heading that means the case against it.
    // Null when the recommendation is to stay put; `nextLevelUp` answers there.
    recMove: (incLevel != null && Math.abs(cl - incLevel) > 1e-9)
      ? marginalMove(levels, Math.min(incLevel, cl), Math.max(incLevel, cl), stackKey)
      : null,
    recMoveDirection: (incLevel == null || Math.abs(cl - incLevel) < 1e-9) ? null
      : (cl > incLevel ? "up" : "down"),
    cases: { up: caseUp, down: caseDown },
    endorsements: {
      sco: { offered: !!chosen.sco, recommended: recSco, ...scoEcon,
             econLabel: ECON_LABEL[scoEcon.grade], netAcre: chosen.sco ? r2(chosen.sco.netAcre) : null,
             covPerAc: chosen.sco ? r2(chosen.sco.covPerAc) : null },
      eco: { offered: !!chosen.eco, recommended: recEco, ...ecoEcon,
             econLabel: ECON_LABEL[ecoEcon.grade], netAcre: chosen.eco ? r2(chosen.eco.netAcre) : null,
             covPerAc: chosen.eco ? r2(chosen.eco.covPerAc) : null },
      areaFitLabel: FIT_LABEL[fit.fit],
    },
    basisRisk,
    ecoLevelInvariant: ecoIsLevelInvariant(levels),
    conflict,
    downUncertain,
    downUncertaintyWarning: downUncertain
      ? "This drop rests on the grower accepting basis risk, not on a measured area fit — " + fit.reasons.join("; ") + "."
      : null,
    farmerAcceptsBasisRisk: accepts,
    // the two questions, as fields rather than only prose
    whyNot85: Math.abs(cl - 0.85) < 1e-9 ? null : bandSentence(0.80),
    whyNot75: Math.abs(cl - 0.75) < 1e-9 ? null : bandSentence(0.75),
    // ── the band decision, additive: nothing existing reads these ────────────
    bandDecision: bandResult ? {
      level: bandResult.level, held: !!bandResult.held,
      shortHistory: !!bandResult.shortHistory, belowRange: !!bandResult.belowRange,
      decidedBy: bandResult.decidedBy || null, restsOnIncumbent: !!bandResult.restsOnIncumbent,
      ppHeld: !!bandResult.ppHeld,
      // ⚠ THIS PROJECTION IS A WHITELIST, so a field added to `bandResult` does not
      // reach a caller until it is named here. The down-move refusal was set on the
      // object and read as `undefined` by everything downstream — the guard worked and
      // was invisible, which is the worst of both. `level` below is still the WALK's
      // own answer; `cl` on the result is what was recommended after the refusal.
      downRefused: bandResult.downRefused || null,
      bands: bandResult.bands, trace: bandResult.trace, why: bandResult.why,
    } : null,
    yieldRecord: profile.usable ? {
      years: profile.n, cv: profile.cv, worst: profile.worst,
      nBelow85: profile.nBelow85, nBelow80: profile.nBelow80, nBelow75: profile.nBelow75,
    } : { years: 0 },
    bandConfidence: bandConfidence(profile, bandResult ? bandResult.bands : []),
    needLabel: NEED_LABEL[need.need], fitLabel: FIT_LABEL[fit.fit],
    why,
  };
}
